import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { useSelector } from 'react-redux';
import { StateRedux } from '../../../store/type-redux';
import {
  Modal, Switch, InputUnform, SelectUnform,
} from '../../../components';
import { api, alerts, doRefreshToken } from '../../../utils';
import { Usuario, FormularioUsuario, Permissao } from '../../../@types/exports';
import validarErroForm from './validar-erro-form';

interface Props {
  atualizarLista: () => void;
  itemParaEditar?: Usuario;
  permissoes: Permissao[];
}
const Texto = (textoToReplace: any) => {
  const { t } = useTranslation();
  return (
    t(`usuarios.${textoToReplace}`)
  );
};
const HtmlToast = (texto: any) => {
  const { textoToReplace } = texto;
  return (
    <div>
      {Texto(textoToReplace)}
    </div>
  );
};
const FormCadastroUsuario = ({ atualizarLista, itemParaEditar, permissoes }: Props) => {
  // Verifica se é necessário realizar o refresh token
  doRefreshToken.getRefreshToken();
  const formRef = useRef<FormHandles>(null);
  const { realm: { nameRealm } } = useSelector((state: StateRedux) => state);
  const [usuario, setUsuario] = useState<Usuario | undefined>(itemParaEditar);
  const [loadButton, setLoadButton] = useState<boolean>(false);
  const [permissao, setPermissao] = useState<any[]>([]);
  const options = permissoes
    // .map((p) => ({ value: p.name.toLocaleLowerCase(), label: p.name }));
    .map((p) => ({ value: p.name, label: p.name }));

  const closeModal = () => {
    const modal = document.getElementById('modal-close-modal-cadastro-usuario');
    modal?.click();
  };

  const handleCadastro = async (data: FormularioUsuario) => {
    // Verifica se é necessário realizar o refresh token
    doRefreshToken.getRefreshToken();
    try {
      formRef.current?.setErrors({});

      await validarErroForm(data);

      setLoadButton(true);

      const { name } = data;
      console.log(`Name Realm ${nameRealm}`);
      const create = {
        user: {
          ...data,
          firstName: String(name).trim().split(' ')[0],
          lastName: String(name).trim().split(' ')[1],
          username: data.username.toLowerCase(),
          totp: false,
          permissions: data.permissions,
        },
      };
      const userNamePass = create.user.username;
      delete create.user.name;
      const returnCadastro = await api.createUsuario(create);
      // Envia email
      await api.enviarEmail({
        username: userNamePass,
        message: `senha provisória criada ${returnCadastro.data.msg.split(':')[1].slice(0, 11)}, usuário: ${userNamePass}`,
      });
      toast.success(
        <div>
          &nbsp;
          {userNamePass}
          <HtmlToast textoToReplace="cadastradoSenha" />
        </div>,
      );
      atualizarLista();
      closeModal();
    } catch (error: any) {
      let resposta = '';
      console.log('Erro ao cadastrar', error);
      if (error.response) {
        if (error.response.data.errorMessage) {
          resposta = error.response.data.errorMessage;
        } else {
          resposta = error.response.data;
        }
        alerts.exibirToastWarn(resposta);
      }
    } finally {
      setLoadButton(false);
    }
  };

  const handleEditar = async (data: FormularioUsuario) => {
    // Verifica se é necessário realizar o refresh token
    doRefreshToken.getRefreshToken();
    try {
      if (!itemParaEditar) {
        toast.warning(
          <div>
            <HtmlToast textoToReplace="naoEncontrado" />
          </div>,
        );
        return;
      }

      formRef.current?.setErrors({});
      await validarErroForm(data);
      setLoadButton(true);

      const { name } = data;
      const update = {
        ...data,
        firstName: String(name).trim().split(' ')[0],
        lastName: String(name).trim().split(' ')[1],
        totp: false,
        id: itemParaEditar.id,
        permissions: data.permissions,
      };

      delete update.name;
      delete update.password;
      closeModal();
      // Deleta todas as permissões
      api.removerPermissoesUsuario(itemParaEditar.id);
      await api.updateUsuario(itemParaEditar.id, update);

      atualizarLista();
      closeModal();

      toast.success(
        <div>
          {itemParaEditar.firstName}
          &nbsp;
          <HtmlToast textoToReplace="form.alteradoComSucesso" />
        </div>,
      );
    } catch (error) {
      alerts.exibirErrorCatch(error, formRef);
    } finally {
      setLoadButton(false);
    }
  };
  const getPermissions = async (id: string) => {
    // Verifica se é necessário realizar o refresh token
    doRefreshToken.getRefreshToken();
    const { data: { msg } } = await api.getPermissaoPorUsuario(id);
    return msg;
  };
  useEffect(() => {
    setUsuario(itemParaEditar);
    if (itemParaEditar?.id) {
      getPermissions(itemParaEditar.id)
        .then((result) => {
          setPermissao(result);
        });
    }
  }, [itemParaEditar]);

  return (
    <Form ref={formRef} onSubmit={itemParaEditar ? handleEditar : handleCadastro}>
      <Modal
        id="modal-cadastro-usuario"
        title={itemParaEditar ? Texto('form.editar') : Texto('form.cadastro')}
        confirmButtonText={itemParaEditar ? Texto('botoes.editar') : Texto('botoes.cadastrar')}
        cancelButtonText={Texto('botoes.voltar')}
        loadButton={loadButton}
        size="lg"
      >
        <div className="m-portlet__body">
          <div className="form-group m-form__group row">
            <span className="col-3 col-form-span">
              {Texto('form.nomecompleto')}
              <span className="text-danger">*</span>
            </span>
            <div className="col-8">
              <InputUnform
                key={usuario?.id}
                name="name"
                className="form-control m-input"
                type="text"
                defaultValue={(
                  usuario ? `${usuario.firstName} ${usuario.lastName}` : ''
                )}
              />
            </div>
          </div>
          <div className="form-group m-form__group row">
            <span className="col-3 col-form-span">
              Username
              <span className="text-danger">*</span>
            </span>
            <div className="col-8">
              <InputUnform
                key={usuario?.id}
                name="username"
                className="form-control m-input"
                type="text"
                defaultValue={usuario?.username}
                id="example-search-input"
              />
            </div>
          </div>
          <div className="form-group m-form__group row">
            <span className="col-3 col-form-span">
              Email
              <span className="text-danger">*</span>
            </span>
            <div className="col-8">
              <InputUnform
                key={usuario?.id}
                name="email"
                className="form-control m-input"
                type="email"
                defaultValue={usuario?.email}
                id="example-email-input"
              />
            </div>
          </div>
          {/* <div className="form-group m-form__group row">
            <span className="col-3 col-form-span">
              Password
            </span>
            <div className="col-8">
              <InputUnform
                key={usuario?.id}
                className="form-control m-input"
                type="password"
                name="password"
                defaultValue={usuario?.password}
                id="example-password-input"
              />
            </div>
          </div> */}
          <div className="form-group m-form__group row">
            <span className="col-3 col-form-span">
              {Texto('form.habilitado')}
            </span>
            <div className="col-8">
              <Switch
                key={usuario?.id}
                name="enabled"
                id="enable"
                defaultChecked={Boolean(usuario?.enabled)}
              />
            </div>
          </div>
          <div className="form-group m-form__group row">
            <span className="col-3 col-form-span">
              {Texto('form.emailVerificado')}
            </span>
            <div className="col-8">
              <Switch
                key={usuario?.id}
                name="emailVerified"
                id="email-erificado"
                defaultChecked={Boolean(usuario?.emailVerified)}
              />
            </div>
          </div>
          <div className="form-group m-form__group row">
            <span className="col-3 col-form-span">
              {Texto('perfil')}
            </span>
            <div className="col-8">
              <SelectUnform
                key={usuario?.id}
                placeholder={Texto('form.selecionePerfil')}
                name="permissions"
                isMulti
                // optionDefault={(options.find((o) => o.value === usuario?.lastName))}
                optionDefault={
                  permissao
                    .map((element: any) => ({
                      label: element.name,
                      value: element.name,
                    }))
                }
                options={options}
              />
            </div>
          </div>
        </div>
      </Modal>
    </Form>
  );
};

FormCadastroUsuario.defaultProps = {
  itemParaEditar: undefined,
};

export default FormCadastroUsuario;
